import React, { useEffect, useRef, useState, useCallback } from "react";

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/** Consent Mode default = denied BEFORE any tag */
function setConsentDefaultDenied() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    "consent.default": { ad_storage: "denied", analytics_storage: "denied" },
  });
  window.gtag = function gtag(...args: any[]) {
    window.dataLayer.push(args);
  };
  window.gtag("consent", "default", {
    ad_storage: "denied",
    analytics_storage: "denied",
  });
}

/** Update Consent Mode */
function updateConsent(granted: boolean) {
  const payload = granted
    ? { ad_storage: "granted", analytics_storage: "granted" }
    : { ad_storage: "denied", analytics_storage: "denied" };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "consent.update", ...payload });
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", payload);
  }
}

/** Load GTM only after consent = granted */
async function enableGTM(gtmContainerId?: string, dataLayerName = "dataLayer") {
  if (!gtmContainerId) return;

  setConsentDefaultDenied();

  const w = window as any,
    d = document,
    s = "script",
    l = dataLayerName,
    i = gtmContainerId;

  w[l] = w[l] || [];
  w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

  const f = d.getElementsByTagName(s)[0]!;
  const j = d.createElement(s) as HTMLScriptElement;
  const dl = l !== "dataLayer" ? `&l=${l}` : "";

  j.async = true;
  j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
  j.id = "gtm-loader";
  f.parentNode!.insertBefore(j, f);

  updateConsent(true);
}

const LS_KEY = "cookieConsent"; // "accepted" | "declined"

type CookieConsentProps = {
  gtmId?: string;
  dataLayerName?: string;
  brandGradient?: string;
  brandBg?: string;
  linkColor?: string;
};

export default function CookieConsent({
  gtmId = "GTM-WXH524ZW",
  dataLayerName = "dataLayer",
  brandGradient = "linear-gradient(90deg,#16C784,#2F80ED)",
  brandBg = "#0F172A",
  linkColor = "#22D3EE",
}: CookieConsentProps) {
  const [visible, setVisible] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  // First paint: set default denied and decide visibility
  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY);
    setConsentDefaultDenied();

    if (saved === "accepted") {
      setVisible(false);
      enableGTM(gtmId, dataLayerName).catch(() => {});
    } else if (saved === "declined") {
      setVisible(false);
      updateConsent(false);
    } else {
      setVisible(true);
      document.body.style.overflow = "hidden";
      updateConsent(false);
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [gtmId, dataLayerName]);

  // Focus trap start (light)
  useEffect(() => {
    if (visible && modalRef.current) {
      modalRef.current.setAttribute("tabindex", "-1");
      modalRef.current.focus();
    }
  }, [visible]);

  const decide = useCallback(
    async (value: "accepted" | "declined") => {
      localStorage.setItem(LS_KEY, value);
      setVisible(false);
      document.body.style.overflow = "";

      if (value === "accepted") {
        await enableGTM(gtmId, dataLayerName).catch(() => {});
      } else {
        updateConsent(false);
      }
    },
    [gtmId, dataLayerName]
  );

  if (!visible) return null;

  // Inline styles
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.72)",
    zIndex: 9998,
  };
  const wrapStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    zIndex: 9999,
  };
  const modalStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: 560,
    borderRadius: 16,
    background: brandBg,
    color: "#fff",
    padding: "24px",
    boxShadow: "0 20px 60px rgba(0,0,0,.35)",
    outline: "none",
    position: "relative",
  };
  const btnPrimary: React.CSSProperties = {
    padding: "11px 18px",
    borderRadius: 10,
    border: "0",
    color: "#fff",
    fontWeight: 700,
    backgroundImage: brandGradient,
    cursor: "pointer",
  };
  const btnSecondary: React.CSSProperties = {
    padding: "11px 18px",
    borderRadius: 10,
    border: "0",
    background: "#4B5563",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  };
  const linkStyle: React.CSSProperties = {
    color: linkColor,
    textDecoration: "underline",
  };
  const badgeStyle: React.CSSProperties = {
    fontSize: 12,
    padding: "4px 8px",
    borderRadius: 999,
    background: "rgba(22,199,132,.15)",
    color: "#16C784",
    fontWeight: 700,
    marginLeft: 8,
  };
  const closeStyle: React.CSSProperties = {
    position: "absolute",
    top: 10,
    right: 12,
    background: "transparent",
    border: "0",
    color: "#9CA3AF",
    fontSize: 20,
    cursor: "pointer",
  };

  return (
    <>
      {/* Optionnel : clic sur l’overlay = Decline */}
      <div style={overlayStyle} aria-hidden="true" onClick={() => decide("declined")} />

      <div
        style={wrapStyle}
        aria-modal="true"
        role="dialog"
        aria-labelledby="cc-title"
        aria-describedby="cc-desc"
      >
        <div ref={modalRef} style={modalStyle}>
          <button
            aria-label="Close"
            onClick={() => decide("declined")}
            style={closeStyle}
          >
            ✕
          </button>

          <h3
            id="cc-title"
            style={{
              fontSize: 24,
              fontWeight: 800,
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
            }}
          >
            Privacy & Cookies <span style={badgeStyle}>EU / GDPR</span>
          </h3>

          <p id="cc-desc" style={{ opacity: 0.92, lineHeight: 1.6, marginBottom: 10 }}>
            We use cookies to improve your experience and measure performance. You can{" "}
            <strong>accept</strong> or <strong>continue without cookies</strong>. Learn
            more in our{" "}
            <a href="/privacy-policy" style={linkStyle} target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
            ,{" "}
            <a href="/terms-of-service" style={linkStyle} target="_blank" rel="noreferrer">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="/terms-of-service" style={linkStyle} target="_blank" rel="noreferrer">
              Terms of Service
            </a>			
            .
          </p>

          <ul style={{ opacity: 0.85, fontSize: 13, margin: "8px 0 18px 18px" }}>
            <li>Analytics/marketing tags (via GTM) load only after you <strong>Accept</strong>.</li>
            <li>“Close” or “Continue without cookies” = <strong>Decline</strong> (no tracking).</li>
          </ul>

          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button onClick={() => decide("declined")} style={btnSecondary}>
              Continue without cookies
            </button>
            <button onClick={() => decide("accepted")} style={btnPrimary}>
              Accept
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
